<template>
  <div class="tracker-list">
    <template v-if="items.length && !showDetails">
      <!-- outlined card, its heading inside with the New Request action, theme dividers between
           the rows, like the other lists; shown even with no open requests so the action stays -->
      <v-card variant="outlined" class="mb-4">
        <v-list lines="two" class="py-0">
          <div class="d-flex align-center pr-4">
            <v-list-subheader>OPEN REQUESTS ({{ openRequests.length }})</v-list-subheader>
            <v-spacer />
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              prepend-icon="mdi-plus"
              data-test="tracker-new-request"
              @click="$emit('click:new-request')"
            >
              New Request
            </v-btn>
          </div>
          <v-divider />
          <v-list-item v-if="!openRequests.length" class="text-medium-emphasis">
            No open requests for this page
          </v-list-item>
          <template v-for="(item, index) in openRequests" :key="item._id">
            <v-divider v-if="index" />
            <!-- the whole row opens the request; the vote button keeps its own click -->
            <v-list-item
              data-test="tracker-open-request"
              @click="openDetailedView(item)"
            >
              <template #prepend>
                <v-avatar
                  size="36"
                  variant="tonal"
                  :color="item.type === 'feature' ? 'info' : 'error'"
                  :title="item.type === 'feature' ? 'Feature request' : 'Bug'"
                >
                  <v-icon size="20">{{ item.type === 'feature' ? 'mdi-lightbulb-on-outline' : 'mdi-bug-outline' }}</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-wrap">{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $filters.formatJSDateTime(item.created.date) }} by {{ item.created.name }}
              </v-list-item-subtitle>
              <template #append>
                <RequestVoteButton
                  :count="item.votes.length"
                  :voted="isVoted(item.votes)"
                  :loading="isVoteLoading(item._key)"
                  data-test="tracker-vote"
                  @click:vote="$emit('vote', item._key)"
                />
              </template>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
      <template v-if="closedRequests.length">
        <v-card variant="outlined">
          <v-list lines="two" class="py-0">
            <v-list-subheader>COMPLETED REQUESTS ({{ closedRequests.length }})</v-list-subheader>
            <v-divider />
            <template v-for="(item, index) in closedRequests" :key="item._id">
              <v-divider v-if="index" />
              <v-list-item @click="openDetailedView(item)">
                <template #prepend>
                  <v-avatar size="36" variant="tonal" color="success">
                    <v-icon size="20">mdi-check</v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="text-wrap">{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.closed">
                  Closed {{ $filters.formatJSDateTime(item.closed.date) }} by {{ item.closed.name }}
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </template>
    </template>
    <template v-else-if="items.length && showDetails">
      <v-card variant="outlined">
        <v-card-title>
          <v-chip
            size="small"
            class="mb-2"
            :class="{ 'text-white': tempItem.status !== 'closed' }"
            :color="tempItem.status === 'closed' ? 'grey-lighten-1' : 'green'"
          >
            {{ tempItem.status }}
          </v-chip>
          <v-spacer />
          <v-btn icon @click="showDetails = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="title mb-5">{{ tempItem.name }}</div>
              <v-divider class="mb-5" />
              <div>
                <div class="overline">Summary</div>
                <span>{{ tempItem.description }}</span>
              </div>
              <div v-if="tempItem.status.toLowerCase() === 'closed'">
                <strong>Closed by:</strong> {{ tempItem.closed.name }},
                {{ $filters.formatDateTime(tempItem.closed.date) }} ({{ closedDiff(tempItem) }})
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="overline">Reported By</div>
              {{ tempItem.created.name }},
              {{ $filters.formatDateTime(tempItem.created.date) }}
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="overline">Screenshot(s)</div>
              <span v-if="tempItem.screenshots">
                <span
                  v-for="screenshot in tempItem.screenshots"
                  :key="screenshot.url"
                >
                  <v-card variant="outlined" class="ma-2">
                    <v-img max-height="300px" :src="screenshot.url" />
                  </v-card>
                </span>
              </span>
              <span v-else class="na">n/a</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="overline">ID</div>
              {{ tempItem._key }}
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
    <div v-else class="tracker-list__empty">
      <p class="mb-4">No bugs or features have been submitted for this page</p>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        data-test="tracker-new-request"
        @click="$emit('click:new-request')"
      >
        New Request
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import { BugTrackerMixin } from '@/bundles/Requests/mixins/BugTrackerMixin';

import RequestVoteButton from '@/bundles/Requests/components/RequestVoteButton.vue';

export default {
  name: 'TrackerWrapperList',

  components: { RequestVoteButton },

  mixins: [BugTrackerMixin],

  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['vote', 'click:new-request'],

  data: () => ({
    showDetails: false,
    tempItem: null,
  }),

  computed: {

    openRequests () {
      return this.items.filter((item) => item.status !== 'closed')
    },
    closedRequests () {
      return this.items.filter((item) => item.status === 'closed')
    },
  },

  methods: {
    openDetailedView (item) {
      this.showDetails = true
      this.tempItem = item
    },
    closedDiff (item) {
      if (item.status === 'closed') {
        const start = moment(item.created.date)
        const end = moment(item.closed.date)
        const diff = end.diff(start, 'hours')
        if (diff > 24) {
          const days = parseInt(diff / 24)
          const hours = diff - days * 24
          return (
            days +
            (days > 1 ? ' days, ' : ' day, ') +
            hours +
            (hours > 1 ? ' hours' : ' hour')
          )
        }
        return diff + ' hours'
      }
      return null
    },
  },
}
</script>

<style scoped></style>
